import { request } from "./http.js";
import { SlickPayError } from "./errors.js";
import { compact, generateRib, isPaid, pick, unwrap } from "./utils.js";

export const SANDBOX_BASE_URL = "https://devapi.slick-pay.com/api/v2/";
export const PRODUCTION_BASE_URL = "https://prodapi.slick-pay.com/api/v2/";

/**
 * Client for the SlickPay v2 API — the gateway that fronts SATIM, so payers
 * settle with an Edahabia or CIB card on a hosted SATIM page.
 *
 * ⚠️ The API key is a bearer credential. Calling this from browser code bundles
 * the key into your JS where anyone can read it. That is tolerable for a
 * sandbox key; for a live key, run this client on your server and expose your
 * own endpoints to the frontend. See "Security" in the README.
 */
export class SlickPay {
  /**
   * @param {object} options
   * @param {string} options.publicKey  Bearer token from your SlickPay dashboard.
   * @param {string} [options.account]  Default merchant account uuid for invoices.
   * @param {boolean} [options.sandbox=true]  Selects the dev/prod base url.
   * @param {string} [options.baseUrl]  Overrides `sandbox` entirely.
   * @param {number} [options.timeout=30000]  Per-request timeout in ms.
   * @param {typeof fetch} [options.fetch]  Custom fetch (tests, Node < 18).
   */
  constructor(options = {}) {
    const { publicKey, account, sandbox = true, baseUrl, timeout = 30000, fetch: fetchImpl } = options;

    if (!publicKey) {
      throw new SlickPayError("SlickPay: `publicKey` is required.", { code: "config" });
    }

    this.publicKey = publicKey;
    this.account = account;
    this.baseUrl = baseUrl ?? (sandbox ? SANDBOX_BASE_URL : PRODUCTION_BASE_URL);
    this.timeout = timeout;
    this.fetchImpl = fetchImpl;

    this.contacts = {
      /** POST users/contacts — `rib` is generated when omitted. */
      create: (data) =>
        this.request("POST", "users/contacts", {
          body: compact({ rib: generateRib(), ...data }),
        }).then(unwrap),
      /** GET users/contacts/{uuid} */
      get: (uuid) => this.request("GET", `users/contacts/${uuid}`).then(unwrap),
      /** GET users/contacts */
      list: (params = {}) => this.request("GET", "users/contacts", { query: params }),
      /** PUT users/contacts/{uuid} */
      update: (uuid, data) =>
        this.request("PUT", `users/contacts/${uuid}`, { body: compact(data) }).then(unwrap),
      /** DELETE users/contacts/{uuid} */
      remove: (uuid) => this.request("DELETE", `users/contacts/${uuid}`),
    };

    this.invoices = {
      /**
       * POST users/invoices — creates the payable invoice and returns the SATIM
       * redirect url. Falls back to the client's default `account` when the
       * caller does not pass one.
       */
      create: async (data) => {
        const account = data.account ?? this.account;
        if (!account) {
          throw new SlickPayError(
            "SlickPay: an `account` uuid is required to create an invoice — pass it to the constructor or per call.",
            { code: "config" }
          );
        }
        return unwrap(
          await this.request("POST", "users/invoices", { body: compact({ ...data, account }) })
        );
      },
      /** GET users/invoices/{id} */
      get: (id) => this.request("GET", `users/invoices/${id}`).then(unwrap),
      /** GET users/invoices */
      list: ({ offset = 0, page = 1, fake } = {}) =>
        this.request("GET", "users/invoices", { query: compact({ offset, page, fake }) }),
    };
  }

  /**
   * Creates the contact and the invoice in one step and hands back the url to
   * redirect the payer to. This is the whole "start a payment" flow.
   *
   * @returns {Promise<{paymentUrl: string, invoiceId: string|number, contactUuid: string, invoice: object, contact: object}>}
   */
  async checkout({ customer, contact: existingContact, amount, returnUrl, items, account, ...rest }) {
    if (!(amount > 0)) {
      throw new SlickPayError(`SlickPay: checkout needs a positive \`amount\` (got ${amount}).`, {
        code: "config",
      });
    }
    if (!returnUrl) {
      throw new SlickPayError(
        "SlickPay: checkout needs a `returnUrl` — SATIM sends the payer back there after paying.",
        { code: "config" }
      );
    }

    let contact = null;
    let contactUuid = existingContact ?? null;
    if (!contactUuid) {
      if (!customer) {
        throw new SlickPayError(
          "SlickPay: checkout needs either a `customer` to create or an existing `contact` uuid.",
          { code: "config" }
        );
      }
      contact = await this.contacts.create(customer);
      contactUuid = pick(contact, "uuid", "id");
      if (!contactUuid) {
        throw new SlickPayError("SlickPay: contact created but no uuid came back.", {
          code: "response",
          body: contact,
        });
      }
    }

    const invoice = await this.invoices.create({
      amount,
      account,
      contact: contactUuid,
      url: returnUrl,
      items: items ?? [{ name: "Payment", price: amount, quantity: 1 }],
      ...rest,
    });

    const paymentUrl = pick(invoice, "url", "redirect_url");
    const invoiceId = pick(invoice, "id", "invoice_id");
    if (!paymentUrl || !invoiceId) {
      throw new SlickPayError("SlickPay: invoice created but no payment url/id came back.", {
        code: "response",
        body: invoice,
      });
    }

    return { paymentUrl, invoiceId, contactUuid, invoice, contact };
  }

  /**
   * Re-reads an invoice after the payer returns and reports whether it is paid.
   * Always verify server-side data like this — never trust the redirect itself.
   *
   * @returns {Promise<{paid: boolean, invoice: object}>}
   */
  async verify(invoiceId) {
    const invoice = await this.invoices.get(invoiceId);
    return { paid: isPaid(invoice), invoice };
  }

  /**
   * Escape hatch for endpoints this package does not model yet (transfers,
   * anything SlickPay adds later). Returns the parsed body untouched.
   */
  request(method, path, { query, body, headers } = {}) {
    return request({
      baseUrl: this.baseUrl,
      path,
      method,
      token: this.publicKey,
      query,
      body,
      headers,
      timeout: this.timeout,
      fetchImpl: this.fetchImpl,
    });
  }
}
